import { configureStore } from '@reduxjs/toolkit';
import { candidateSubmissionGuardMiddleware } from 'helpers/candidate_application/candidate-submission-guard';

import { adminLogsApi } from 'api/admin-logs-api';
import AppReducer from 'api/app-slice';
import { auditLogsApi } from 'api/audit-logs-api';
import { authenticationApi } from 'api/authentication-api';
import AuthenticationReducer from 'api/authentication-slice';
import { billingApi } from 'api/billing-api';
import { brandApi } from 'api/brands-api';
import BrandsReducer from 'api/brands-slice';
import BundlesReducer from 'api/bundles-slice';
import CandidateApplicationReducer from 'api/candidate-application-slice';
import { candidateChecksApi } from 'api/candidate-checks-api';
import { candidateChecksValuesApi } from 'api/candidate-checks-values-api';
import CustomFieldReducer from 'api/candidate-custom-fields-slice';
import { candidatePortalApi } from 'api/candidate-portal-api';
import CandidatePortalReducer from 'api/candidate-portal-slice';
import { candidatesApi } from 'api/candidates-api';
import CandidateReducer from 'api/candidates-slice';
import { checkRenewalsApi } from 'api/check-renewals-api';
import { checkTypePricingApi } from 'api/check-type-pricing-api';
import { checkTypeApi } from 'api/check-types-api';
import { companyApi } from 'api/company-api';
import { companyChecksApi } from 'api/company-checks-api';
import CompanyChecksReducer from 'api/company-checks-slice';
import { companyGroupsApi } from 'api/company-groups-api';
import CompanyReducer from 'api/company-slice';
import { directoryGroupsApi } from 'api/directory-groups-api';
import { documentRequestsApi } from 'api/document-requests-api';
import { emailTemplatesApi } from 'api/email-templates-api';
import { employeesApi } from 'api/employees-api';
import { formsApi } from 'api/forms-api';
import FormsReducer from 'api/forms-slice';
import { idDocumentTypesApi } from 'api/id-document-types-api';
import { idUploadDetailsApi } from 'api/id-upload-details-api';
import { integrationApi } from 'api/integration-api';
import { intercomConversationsApi } from 'api/intercom-conversations-api';
import { linkedinApi } from 'api/linkedin-api';
import { notificationSettingsApi } from 'api/notification-settings-slice';
import { oauthClientApi } from 'api/oauth-client-api';
import { partnerApi } from 'api/partner-api';
import { partnerV1Api } from 'api/partner-v1-api';
import { partnerWebhookLogsApi } from 'api/partner-webhook-logs-api';
import PartnerConfigReducer from 'api/partners-config-slice';
import { permissionsApi } from 'api/permissions-api';
import { purchasesApi } from 'api/purchases-api';
import { refereesApi } from 'api/referees-api';
import { renewalsConfigApi } from 'api/renewals-config-api';
import { reportingApi } from 'api/reporting-api';
import { reportsApi } from 'api/reports-api';
import RolesReducer from 'api/roles-slice';
import { savedViewsApi } from 'api/saved-views-api';
import { servicesApi } from 'api/services-api';
import { subChecksApi } from 'api/sub-checks-api';
import { supportAccessApi } from 'api/support-access-api';
import TagReducer from 'api/tags-slice';
import TeamsReducer from 'api/teams-slice';
import { uploadApi } from 'api/upload-api';
import { userApi } from 'api/user-api';
import UserPermissionsReducer from 'api/user-permissions-slice';
import UserReducer from 'api/user-slice';

export const store = configureStore({
  reducer: {
    authentication: AuthenticationReducer,
    candidates: CandidateReducer,
    bundles: BundlesReducer,
    company: CompanyReducer,
    permissions: UserPermissionsReducer,
    user: UserReducer,
    app: AppReducer,
    forms: FormsReducer,
    companyChecks: CompanyChecksReducer,
    tags: TagReducer,
    candidateApplication: CandidateApplicationReducer,
    teams: TeamsReducer,
    roles: RolesReducer,
    brands: BrandsReducer,
    customFields: CustomFieldReducer,
    partnersConfig: PartnerConfigReducer,
    candidatePortal: CandidatePortalReducer,
    [candidatePortalApi.reducerPath]: candidatePortalApi.reducer,
    [idDocumentTypesApi.reducerPath]: idDocumentTypesApi.reducer,
    [candidatesApi.reducerPath]: candidatesApi.reducer,
    [candidateChecksApi.reducerPath]: candidateChecksApi.reducer,
    [renewalsConfigApi.reducerPath]: renewalsConfigApi.reducer,
    [checkRenewalsApi.reducerPath]: checkRenewalsApi.reducer,
    [companyApi.reducerPath]: companyApi.reducer,
    [userApi.reducerPath]: userApi.reducer,
    [billingApi.reducerPath]: billingApi.reducer,
    [companyGroupsApi.reducerPath]: companyGroupsApi.reducer,
    [uploadApi.reducerPath]: uploadApi.reducer,
    [notificationSettingsApi.reducerPath]: notificationSettingsApi.reducer,
    [formsApi.reducerPath]: formsApi.reducer,
    [checkTypeApi.reducerPath]: checkTypeApi.reducer,
    [candidateChecksValuesApi.reducerPath]: candidateChecksValuesApi.reducer,
    [companyChecksApi.reducerPath]: companyChecksApi.reducer,
    [adminLogsApi.reducerPath]: adminLogsApi.reducer,
    [reportsApi.reducerPath]: reportsApi.reducer,
    [authenticationApi.reducerPath]: authenticationApi.reducer,
    [oauthClientApi.reducerPath]: oauthClientApi.reducer,
    [brandApi.reducerPath]: brandApi.reducer,
    [refereesApi.reducerPath]: refereesApi.reducer,
    [subChecksApi.reducerPath]: subChecksApi.reducer,
    [partnerApi.reducerPath]: partnerApi.reducer,
    [partnerWebhookLogsApi.reducerPath]: partnerWebhookLogsApi.reducer,
    [linkedinApi.reducerPath]: linkedinApi.reducer,
    [auditLogsApi.reducerPath]: auditLogsApi.reducer,
    [integrationApi.reducerPath]: integrationApi.reducer,
    [partnerV1Api.reducerPath]: partnerV1Api.reducer,
    [permissionsApi.reducerPath]: permissionsApi.reducer,
    [purchasesApi.reducerPath]: purchasesApi.reducer,
    [reportingApi.reducerPath]: reportingApi.reducer,
    [checkTypePricingApi.reducerPath]: checkTypePricingApi.reducer,
    [servicesApi.reducerPath]: servicesApi.reducer,
    [employeesApi.reducerPath]: employeesApi.reducer,
    [documentRequestsApi.reducerPath]: documentRequestsApi.reducer,
    [intercomConversationsApi.reducerPath]: intercomConversationsApi.reducer,
    [idUploadDetailsApi.reducerPath]: idUploadDetailsApi.reducer,
    [savedViewsApi.reducerPath]: savedViewsApi.reducer,
    [emailTemplatesApi.reducerPath]: emailTemplatesApi.reducer,
    [directoryGroupsApi.reducerPath]: directoryGroupsApi.reducer,
    [supportAccessApi.reducerPath]: supportAccessApi.reducer,
  },
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware()
      .prepend(candidateSubmissionGuardMiddleware)
      .concat([
        candidatePortalApi.middleware,
        idDocumentTypesApi.middleware,
        candidatesApi.middleware,
        candidateChecksApi.middleware,
        renewalsConfigApi.middleware,
        checkRenewalsApi.middleware,
        companyApi.middleware,
        userApi.middleware,
        billingApi.middleware,
        companyGroupsApi.middleware,
        uploadApi.middleware,
        notificationSettingsApi.middleware,
        formsApi.middleware,
        checkTypeApi.middleware,
        candidateChecksValuesApi.middleware,
        companyChecksApi.middleware,
        adminLogsApi.middleware,
        reportsApi.middleware,
        authenticationApi.middleware,
        oauthClientApi.middleware,
        brandApi.middleware,
        refereesApi.middleware,
        subChecksApi.middleware,
        partnerApi.middleware,
        partnerWebhookLogsApi.middleware,
        linkedinApi.middleware,
        auditLogsApi.middleware,
        integrationApi.middleware,
        partnerV1Api.middleware,
        permissionsApi.middleware,
        purchasesApi.middleware,
        reportingApi.middleware,
        checkTypePricingApi.middleware,
        servicesApi.middleware,
        employeesApi.middleware,
        documentRequestsApi.middleware,
        intercomConversationsApi.middleware,
        idUploadDetailsApi.middleware,
        savedViewsApi.middleware,
        emailTemplatesApi.middleware,
        directoryGroupsApi.middleware,
        supportAccessApi.middleware,
      ]),
  devTools: process.env.REACT_APP_DEBUG,
});
